import { createContext, useContext, useEffect, useState } from 'react';
import { api } from './api';

/**
 * Company info — canonical source for AutoMarket Colombia's public contact
 * details. Backend `/api/config/company` reads from `settings.platform` so
 * ops can change NIT, phone, address or social handles without a deploy.
 *
 * The hook caches the payload in memory (single fetch per app boot) and
 * exposes the whatsapp deep-link, socials array and a helper for the
 * `tel:` and `mailto:` schemes.
 */
const CompanyCtx = createContext({ data: null, loading: true });

const EMPTY = {
  company_legal_name: '',
  company_nit: '',
  company_address: '',
  company_phone: '',
  company_email: '',
  social_instagram: '',
  social_tiktok: '',
  social_facebook: '',
  whatsapp_url: '',
};

export function CompanyProvider({ children }) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.get('/config/company')
      .then((r) => { if (!cancel) setData({ ...EMPTY, ...(r.data || {}) }); })
      .catch(() => { if (!cancel) setData(EMPTY); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  return <CompanyCtx.Provider value={{ data, loading }}>{children}</CompanyCtx.Provider>;
}

export function useCompany() {
  const { data, loading } = useContext(CompanyCtx);
  const phoneDisplay = data.company_phone
    ? data.company_phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3')
    : '';
  const socials = [
    data.social_instagram && { slug: 'instagram', label: 'Instagram', href: data.social_instagram, hover: 'hover:bg-[#E1306C]' },
    data.social_tiktok && { slug: 'tiktok', label: 'TikTok', href: data.social_tiktok, hover: 'hover:bg-black' },
    data.social_facebook && { slug: 'facebook', label: 'Facebook', href: data.social_facebook, hover: 'hover:bg-[#1877F2]' },
  ].filter(Boolean);
  return {
    ...data,
    loading,
    phoneDisplay,
    telHref: data.company_phone ? `tel:+57${data.company_phone.replace(/\D/g, '')}` : '',
    mailtoHref: data.company_email ? `mailto:${data.company_email}` : '',
    socials,
  };
}
